import React, { useCallback } from 'react'
import { Pagination } from '@dataplace.ai/ui-components/molecules'
import { ImageWithFallback, Tooltip } from '@dataplace.ai/ui-components/atoms'
import { Table } from '@dataplace.ai/ui-components/organisms'
import { useTranslation } from 'react-i18next'
import { ReactComponent as ClockIcon } from '@dataplace.ai/assets/lib/icons/clock.svg'
import MedicalExpandable from './MedicalExpandable'
import { GRID_TEMPLATES, SPECIAL_CATEGORIES } from '../constants'
import { IPaginationTable, IPois, IPoiWithMedicalInfo } from '../@types/IPoisTileData'
import { StyledDistanceSpan } from '../PoisTile.styles'

export function PaginationTable({
  category, poisData, grouped, labels, medicalInfoFeatureAccess = false,
}: IPaginationTable): JSX.Element {
  const { t } = useTranslation()

  const hasSalesArea = category.includes(SPECIAL_CATEGORIES.MULLIEZ_SUFFIX)
  const rowsWithSalesArea = medicalInfoFeatureAccess && category === SPECIAL_CATEGORIES.HEALTHCARE_UNIT
    ? GRID_TEMPLATES.HEALTHCARE_WITH_MEDICAL
    : hasSalesArea
      ? GRID_TEMPLATES.WITH_SALES_AREA
      : GRID_TEMPLATES.DEFAULT

  const filteredLabels = labels.filter(label =>
    label !== 'placeme.pois_tile.table_label_5' || hasSalesArea)

  const renderTableComponent = useCallback((
    data: IPois[],
  ) => {
    const newData = data
    const content: (JSX.Element | string | null)[][] = []

    newData.forEach((value) => {
      content.push([
        <span
          key={value.brand + value.address}
          className={value?.workingHours ? 'bank' : undefined}
        >
          <Tooltip
            content={value.brand}
            position='right center'
          >
            <ImageWithFallback
              key={value.logo}
              alt={value.brand}
              fallbackSrc='assets/icons/logoPlaceholder.svg'
              height='22px'
              src={value.logo}
              width='22px'
            />
            {' '}
            {value.brand}
          </Tooltip>
          {value?.workingHours
            ? (
              <Tooltip
                content={(
                  <ul>
                    {Object.entries(value?.workingHours).map(([key, val]) => <li key={key}>{`${t(`placeme.pois.working_hours.day.${key}`)}: ${val}`}</li>)}
                  </ul>
                )}
                header={t('placeme.pois.working_hours')}
                position='right center'
              >
                <ClockIcon
                  height='18px'
                  width='18px'
                />
              </Tooltip>
            )
            : null}
        </span>,
        <span key={value.address}>
          {grouped
            ? value.count
            : (
              <Tooltip
                content={value.address}
                position='right center'
              >
                {value.address}
              </Tooltip>
            )}
        </span>,
        ...(hasSalesArea && !grouped ? [<span key={value.salesArea}>{value.salesArea}</span>] : []),
        <StyledDistanceSpan key={value.distance}>
          {(value.distance * 1000).toFixed(0)}
        </StyledDistanceSpan>,
      ])
    })

    return (
      <Table
        containerClassName='pdf-poi-tile-table'
        content={content as JSX.Element[][]}
        expandableComponent={(newData.map(value => {
          const canExpand = value.category === SPECIAL_CATEGORIES.HEALTHCARE_UNIT
            && medicalInfoFeatureAccess && !grouped
          if (canExpand) {
            const additionalInfo = (value as IPoiWithMedicalInfo).poiInfo?.additionalInfo
            return (
              <MedicalExpandable
                key={value.pointId}
                nfzContract={additionalInfo?.nfzContract ?? false}
                specification={additionalInfo?.specification ?? []}
                unitSpecialityCells={additionalInfo?.unitSpecialityCells ?? []}
              />
            )
          }
          return undefined
        }).filter(Boolean) as JSX.Element[])}
        gap='1rem'
        headerTemplate={grouped ? '6fr 6fr 4fr' : rowsWithSalesArea}
        labels={[
          <span
            key={category}
            style={{
              whiteSpace: 'nowrap',
            }}
          >
            {t(`placeme.pois_tile.category.${category?.toLocaleLowerCase().split(/[_ -]+/)
              .join('-')}`)}
          </span>,
          ...filteredLabels
            .map((label: string) => (<span key={label}>{t(label)}</span>)),
        ]}
        pois={grouped}
        rowTemplate={grouped ? '6fr 6fr 4fr' : rowsWithSalesArea}
      />
    )
  }, [t, grouped, hasSalesArea, medicalInfoFeatureAccess, category, filteredLabels, rowsWithSalesArea])

  const paginationRenderComponent = useCallback((
    data: string | string[] | IPois[] | JSX.Element[][] | number[] | string[][] | Record<string, unknown>[],
  ) => renderTableComponent(data as IPois[]), [renderTableComponent])

  return (
    <Pagination
      data={poisData as IPois[]}
      dataLimit={5}
      justifyNavigation='center'
      renderComponent={paginationRenderComponent}
    />
  )
}
